import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import { fosError, fosFunds, formatDate, formatMoney, type ContributionsView } from '../../lib/fosApi';
import ContributionDrawer, { type ContributionMode } from './ContributionDrawer';
import { useFos } from './FosShell';
import { Kpi, ProgressBar } from './Progress';
import { useFinancialGuard } from './useFinancial';
import { EmptyState, ErrorNote, Icon, Section } from './ui';

type Tab = 'pledges' | 'gifts';

export default function FosContributions() {
  const { context } = useFos();
  const { guard, modal } = useFinancialGuard();
  const [year, setYear] = useState('');
  const [campaign, setCampaign] = useState('');
  const [tab, setTab] = useState<Tab>('pledges');
  const [data, setData] = useState<ContributionsView | null>(null);
  const [years, setYears] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [declined, setDeclined] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState<ContributionMode | null>(null);
  const [busyId, setBusyId] = useState('');

  // The campaign years come from the dashboard (aggregate, no acknowledgment); the ledger itself needs the acknowledgment.
  useEffect(() => {
    fosFunds.dashboard({}).then((d) => { setYears(d.years); setYear((y) => y || d.selected.campaignYear || d.years[0] || ''); if (!d.years.length) setLoading(false); }).catch((cause) => { setError(fosError(cause).message); setLoading(false); });
  }, []);

  const load = useCallback(async () => {
    if (!year) return;
    setLoading(true); setError(''); setDeclined(false);
    try { setData(await guard(() => fosFunds.contributions({ campaignYear: year, campaignKey: campaign || null }))); }
    catch (cause) { const c = fosError(cause); if (c.code === 'FOS_FINANCIAL_ACK_REQUIRED') setDeclined(true); else setError(c.message); }
    finally { setLoading(false); }
  }, [year, campaign, guard]);
  useEffect(() => { void load(); }, [load]);

  const confirm = async (id: string, version: number) => {
    setBusyId(id); setError('');
    try { await guard(() => fosFunds.mutate({ operation: 'payment.confirm', paymentId: id, expectedVersion: version })); await load(); }
    catch (cause) { setError(fosError(cause).message); await load(); } finally { setBusyId(''); }
  };

  if (!context.capabilities.viewHistory) return <Section title="Contributions" icon="dollar"><EmptyState icon="lock" title="Contributions are restricted">You do not have access to individual giving records.</EmptyState></Section>;
  const t = data?.totals;
  return (
    <>
      <div className="fos-toolbar">
        <label className="fos-inline">Campaign year<select value={year} onChange={(e) => setYear(e.target.value)} disabled={!years.length}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select></label>
        <label className="fos-inline">Campaign
          <select value={campaign} onChange={(e) => setCampaign(e.target.value)} disabled={!data}>
            <option value="">All campaigns</option>{(data?.campaigns ?? []).map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
          </select>
        </label>
        <span className="fos-count fos-actions">
          <button className="btn" onClick={() => setDrawer({ kind: 'pledge' })}><Icon name="plus" size={14} /> Record pledge</button>
          <button className="btn primary" onClick={() => setDrawer({ kind: 'gift' })}><Icon name="plus" size={14} /> Record gift</button>
        </span>
      </div>
      <ErrorNote message={error} />
      {declined && <Section title="Contributions" icon="lock"><p className="fos-lede">Individual giving is confidential. Acknowledge that your access is logged to view it. <button className="btn primary small" onClick={() => void load()}>Continue</button></p></Section>}
      {loading && !data && !declined && <div className="fos-loading"><Spinner label="Loading contributions" /></div>}
      {!years.length && !loading && <Section title="Contributions" icon="dollar"><EmptyState icon="target" title="No campaign yet">Set up a campaign before recording pledges and gifts. <Link to="/fos/goals">Set up a campaign</Link></EmptyState></Section>}
      {data && t && (
        <>
          <div className="fos-kpis">
            <Kpi label="Received (confirmed)" value={formatMoney(t.confirmedCents)} hint="Counts toward goals" tone="good" />
            <Kpi label="Awaiting confirmation" value={formatMoney(t.reportedCents)} hint="Reported, not yet counted" tone="gold" />
            <Kpi label="Pledged, outstanding" value={formatMoney(t.outstandingCents)} hint="Balances still owed" />
            <Kpi label="Total pledged" value={formatMoney(t.pledgedCents)} tone="muted" />
          </div>
          <div className="fos-tabs-inline" role="tablist">
            <button role="tab" aria-selected={tab === 'pledges'} className={tab === 'pledges' ? 'active' : ''} onClick={() => setTab('pledges')}>Outstanding pledges <small>{data.openPledges.length}{data.truncated.pledges ? '+' : ''}</small></button>
            <button role="tab" aria-selected={tab === 'gifts'} className={tab === 'gifts' ? 'active' : ''} onClick={() => setTab('gifts')}>Gifts <small>{data.payments.length}{data.truncated.payments ? '+' : ''}</small></button>
          </div>
          <div className="fos-card fos-table-card">
            <div className="fos-table-wrap">
              {tab === 'pledges' ? (
                data.openPledges.length === 0 ? <EmptyState icon="check" title="No outstanding pledges">Every pledge in this selection has been received.</EmptyState> : (
                  <table className="fos-table"><thead><tr><th>Donor</th><th>Pledge</th><th>Credited to</th><th className="num">Pledged</th><th>Progress</th><th className="num">Outstanding</th></tr></thead>
                    <tbody>{data.openPledges.map((p) => (
                      <tr key={p.id}>
                        <td><Link to={`/fos/donors/${p.donorId}`} className="fos-donor-name">{p.donorName}</Link><div className="fos-ref">{p.donorRef}</div></td>
                        <td>{p.ref}<div className="subtitle">{p.campaignName} · {p.campaignYear}</div></td>
                        <td>{p.scopeLabel}</td>
                        <td className="num">{formatMoney(p.amountCents)}</td>
                        <td className="fos-bar-cell"><ProgressBar raised={p.confirmedCents} pending={p.paidCents - p.confirmedCents} outstanding={p.outstandingCents} goal={null} /></td>
                        <td className="num"><strong className="owed">{formatMoney(p.outstandingCents)}</strong></td>
                      </tr>))}
                    </tbody></table>)
              ) : (
                data.payments.length === 0 ? <EmptyState icon="dollar" title="No gifts yet">Record the first gift for this selection.</EmptyState> : (
                  <table className="fos-table"><thead><tr><th>Received</th><th>Donor</th><th>Credited to</th><th>Campaign</th><th className="num">Amount</th><th>Status</th><th /></tr></thead>
                    <tbody>{data.payments.map((p) => (
                      <tr key={p.id} className={p.status === 'cancelled' ? 'cancelled' : ''}>
                        <td>{formatDate(p.receivedDate)}<div className="fos-ref">{p.ref}</div></td>
                        <td><Link to={`/fos/donors/${p.donorId}`} className="fos-donor-name">{p.donorName}</Link></td>
                        <td>{p.scopeLabel}</td>
                        <td>{p.campaignName}<div className="subtitle">{p.pledgeRef ? `toward ${p.pledgeRef}` : 'no pledge'}</div></td>
                        <td className="num">{formatMoney(p.amountCents)}</td>
                        <td><span className={`fos-chip ${p.status === 'confirmed' ? 'status-active' : p.status === 'reported' ? 'next' : 'off'}`}>{p.status === 'confirmed' ? 'Confirmed' : p.status === 'reported' ? 'Awaiting confirmation' : 'Cancelled'}</span></td>
                        <td>{p.canConfirm && <button className="btn small primary" disabled={busyId === p.id} onClick={() => void confirm(p.id, p.version)}>Confirm</button>}</td>
                      </tr>))}
                    </tbody></table>)
              )}
            </div>
          </div>
          {(data.truncated.pledges || data.truncated.payments) && <p className="fos-hint">Showing the first 200. Choose a single campaign to narrow the list.</p>}
        </>
      )}
      {drawer && <ContributionDrawer mode={drawer} donor={null} onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); void load(); }} />}
      {modal}
    </>
  );
}
